import { extractApiErrorMessage } from '../../Utils/apiErrorUtils';

const BOOKING_SETTLEMENT_ERROR_MESSAGES: Record<string, string> = {
  BOOKING_ALREADY_SETTLED_FOR_ACCOUNTING: 'Termin jest już rozliczony dla księgowości.',
  BOOKING_SETTLEMENT_MISSING_SALES_INVOICES:
    'Nie można rozliczyć terminu bez co najmniej jednej faktury sprzedażowej.',
  BOOKING_CANCELLED: 'Nie można rozliczyć anulowanego terminu.',
  BOOKING_SETTLEMENT_MIXED_SALES_INVOICE_COMPANIES:
    'Nie można rozliczyć terminu, ponieważ faktury sprzedażowe mają różnych wystawców.',
};

interface ProblemPayloadShape {
  errorCode?: unknown;
  detail?: unknown;
  message?: unknown;
  errors?: unknown;
}

const getErrorStatus = (error: unknown): number | string | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return undefined;
};

const getProblemPayload = (error: unknown): ProblemPayloadShape | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  return data as ProblemPayloadShape;
};

const getProblemErrorCode = (error: unknown): string | undefined => {
  const payload = getProblemPayload(error);
  if (!payload || typeof payload.errorCode !== 'string' || !payload.errorCode.trim()) {
    return undefined;
  }

  return payload.errorCode.trim();
};

const getProblemMessages = (error: unknown): string[] => {
  const payload = getProblemPayload(error);
  if (!payload) {
    return [];
  }

  const messages: string[] = [];

  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    messages.push(payload.detail.trim());
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    messages.push(payload.message.trim());
  }

  if (typeof payload.errors === 'object' && payload.errors !== null) {
    Object.values(payload.errors as Record<string, unknown>).forEach((value) => {
      if (!Array.isArray(value)) {
        return;
      }

      value.forEach((item) => {
        if (typeof item === 'string' && item.trim()) {
          messages.push(item.trim());
        }
      });
    });
  }

  return messages;
};

const mapBackendMessageToOperatorMessage = (message: string): string | null => {
  if (message === 'The term has not ended yet.') {
    return 'Nie można rozliczyć terminu przed jego zakończeniem.';
  }

  if (message === 'The term is already settled for accounting.') {
    return BOOKING_SETTLEMENT_ERROR_MESSAGES.BOOKING_ALREADY_SETTLED_FOR_ACCOUNTING;
  }

  if (message.startsWith('Sales invoices in Draft status:')) {
    const invoiceNumbers = message.replace('Sales invoices in Draft status:', '').trim();
    return invoiceNumbers
      ? `Nie można rozliczyć terminu, ponieważ faktury sprzedażowe są w statusie Draft: ${invoiceNumbers}.`
      : 'Nie można rozliczyć terminu, ponieważ istnieją faktury sprzedażowe w statusie Draft.';
  }

  if (message.startsWith('Purchase invoices in Draft status:')) {
    const invoiceNumbers = message.replace('Purchase invoices in Draft status:', '').trim();
    return invoiceNumbers
      ? `Nie można rozliczyć terminu, ponieważ faktury zakupowe są w statusie Draft: ${invoiceNumbers}.`
      : 'Nie można rozliczyć terminu, ponieważ istnieją faktury zakupowe w statusie Draft.';
  }

  if (message.includes('is not settled for accounting')) {
    return 'Termin nie jest obecnie rozliczony dla księgowości.';
  }

  return null;
};

const getMessageFromStatus = (status: number | string | undefined): string | null => {
  if (status === undefined) {
    return null;
  }

  if (status === 'FETCH_ERROR') {
    return 'Brak połączenia z API. Sprawdź dostępność backendu i sieć.';
  }

  if (status === 'PARSING_ERROR') {
    return 'Odpowiedź API ma nieprawidłowy format.';
  }

  if (status === 401) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }

  if (status === 403) {
    return 'Brak uprawnień do operacji rozliczenia księgowego.';
  }

  if (status === 404) {
    return 'Nie znaleziono wskazanego bookingu lub jego rozliczenia księgowego.';
  }

  if (status === 409) {
    return 'Stan bookingu zmienił się równolegle. Odśwież dane i spróbuj ponownie.';
  }

  if (status === 429) {
    return 'Zbyt wiele żądań. Odczekaj chwilę i ponów próbę.';
  }

  if (typeof status === 'number' && status >= 500) {
    return 'Wystąpił błąd serwera podczas rozliczenia księgowego. Spróbuj ponownie później.';
  }

  return null;
};

export const getBookingSettlementErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  const errorCode = getProblemErrorCode(error);
  if (errorCode && BOOKING_SETTLEMENT_ERROR_MESSAGES[errorCode]) {
    return BOOKING_SETTLEMENT_ERROR_MESSAGES[errorCode];
  }

  const inferredMessage = getProblemMessages(error)
    .map(mapBackendMessageToOperatorMessage)
    .find((message): message is string => Boolean(message));
  if (inferredMessage) {
    return inferredMessage;
  }

  const statusMessage = getMessageFromStatus(getErrorStatus(error));
  if (statusMessage) {
    return statusMessage;
  }

  return extractApiErrorMessage(error, fallbackMessage);
};

export const getBookingSettlementErrorCode = (error: unknown): string | undefined => {
  return getProblemErrorCode(error);
};
